// ============================================================
// backfill - a statblock's pins folded back into its sheet.
//
// A plain statblock is a synthesised sheet plus flat pins: whatever was
// typed after it was made (Toughness bonus 6, wounds 40) sits in
// `overrides`, and the sheet under it still says something else. That
// was harmless while only the flat values were editable. On the live
// sheet it is not: the characteristics strip would show a Toughness the
// pin overrules, and attacks and tests read the sheet alone.
//
// So before a statblock's sheet is first edited, each pin that CAN be
// said by the sheet is moved into it (bonus 6 becomes Toughness 60) and
// the pin is let go. What the sheet cannot say stays a pin: the flag
// traits (Astartes, True Grit, Machine...) are not inverted into catalog
// traits, which carry more than the flag did (roster ADR, v7.0.0).
//
// THE INVARIANT: what the combatant defends with never changes. The new
// overrides are rebuilt as the full difference between the old effective
// sheet and the new sheet's answer, so the projection is equal by
// construction; a move is kept only when it leaves FEWER pins.
//
// Pure, deterministic and idempotent - two clients doing it to the same
// combatant write the same sheet.
// ============================================================
import type { Character, CharacteristicId, DefenderSheet } from '../character/types';
import { getEffectiveUnnaturalMultipliers } from '../character/combatStatsResolver';
import { characteristicPatch } from '../character/sheetPatches';
import { trainedPatch } from '../character/skillTiers';
import { sameValue } from '../canonicalJson';
import { cachedDefenderSheet } from './projection';
import { listPins, splitCarried, type Pin } from './pins';
import type { Combatant } from './types';
import { readVehicleKind } from '../vehicleKind';
import { readFigure } from '../figureKind';

type Overrides = Partial<DefenderSheet>;
type Bag = Record<string, unknown>;
type Move = (sheet: Character, pin: Pin) => Character;

const num = (v: unknown): number => (typeof v === 'number' ? v : 0);

/** The sheet with one field of one characteristic set (Agility also re-derives movement). */
function withCharacteristic(
  sheet: Character,
  id: CharacteristicId,
  field: 'value' | 'unnaturalMultiplier',
  value: number,
): Character {
  return { ...sheet, ...characteristicPatch(sheet, id, field, value) };
}

/** A characteristic's multiplier set to the pinned one. */
const multiplier =
  (id: CharacteristicId): Move =>
  (sheet, pin) =>
    withCharacteristic(sheet, id, 'unnaturalMultiplier', num(pin.value) || 1);

/**
 * A characteristic set from a pinned bonus. Strength and Willpower pins
 * are EFFECTIVE (already multiplied), so they are divided back; one that
 * does not divide evenly cannot be said by the sheet and stays a pin.
 */
const bonus =
  (id: CharacteristicId, effective: boolean): Move =>
  (sheet, pin) => {
    const by = effective ? (getEffectiveUnnaturalMultipliers(sheet)[id] ?? 1) : 1;
    const base = num(pin.value) / by;
    return Number.isInteger(base) ? withCharacteristic(sheet, id, 'value', base * 10) : sheet;
  };

/** A characteristic moved by however far the pin sits from the sheet's figure. */
const shifted =
  (id: CharacteristicId): Move =>
  (sheet, pin) =>
    withCharacteristic(sheet, id, 'value', sheet.characteristics[id].value + num(pin.value) - num(pin.sheetValue));

const trained =
  (skill: 'dodge' | 'parry'): Move =>
  (sheet, pin) => ({ ...sheet, ...trainedPatch(sheet, skill, !!pin.value) });

const armourLocation: Move = (sheet, pin) =>
  pin.leaf
    ? { ...sheet, armour: { ...sheet.armour, locations: { ...sheet.armour.locations, [pin.leaf]: num(pin.value) } } }
    : sheet;

/**
 * Every pin the sheet can say, in the order they are tried. Multipliers
 * come first: a Strength bonus is divided by the multiplier in force.
 * Keyed `key` or `key.leaf`; `armour` takes every location.
 */
const MOVES: [string, Move][] = [
  ['unnaturalToughness', multiplier('tgh')],
  ['unnaturalStrength', multiplier('str')],
  ['unnaturalAgility', multiplier('agi')],
  ['unnaturalWeaponSkill', multiplier('ws')],
  ['unnaturalWillpower', multiplier('wp')],
  ['toughnessBonus', bonus('tgh', false)],
  ['strengthBonus', bonus('str', true)],
  ['willpowerBonus', bonus('wp', true)],
  ['dodge.canDodge', trained('dodge')],
  ['dodge.agility', shifted('agi')],
  ['parry.canParry', trained('parry')],
  ['parry.weaponSkill', shifted('ws')],
  ['armour', armourLocation],
  ['woundsMax', (sheet, pin) => ({ ...sheet, wounds: { ...sheet.wounds, max: num(pin.value) } })],
  ['isHorde', (sheet, pin) => ({ ...sheet, horde: { ...sheet.horde, isHorde: !!pin.value } })],
  ['hordeMaxMagnitude', (sheet, pin) => ({ ...sheet, horde: { ...sheet.horde, maxMagnitude: num(pin.value) } })],
  ['size', (sheet, pin) => ({ ...sheet, size: String(pin.value) })],
  ['bodyType', (sheet, pin) => ({ ...sheet, bodyType: pin.value as Character['bodyType'] })],
  ['fearRating', (sheet, pin) => ({ ...sheet, fearRating: num(pin.value) })],
  ['psyRating', (sheet, pin) => ({ ...sheet, psyRating: num(pin.value) })],
  ['xenos', (sheet, pin) => ({ ...sheet, xenos: !!pin.value })],
  ['tokenDiameter', (sheet, pin) => ({ ...sheet, tokenDiameter: num(pin.value) })],
  ['vehicleKind', (sheet, pin) => withOptional(sheet, 'vehicleKind', readVehicleKind(pin.value))],
  ['figure', (sheet, pin) => withOptional(sheet, 'figure', readFigure(pin.value))],
];

/**
 * The sheet with an optional pin set, or with the key removed when there is none (a vehicle kind, a figure):
 * never a literal undefined - Firestore refuses the whole document (gotcha 2).
 */
function withOptional<K extends 'vehicleKind' | 'figure'>(
  sheet: Character,
  key: K,
  value: Character[K] | null,
): Character {
  if (value) return { ...sheet, [key]: value };
  const { [key]: _dropped, ...rest } = sheet;
  return rest as Character; // the key is optional: a sheet without it is still a sheet
}

const pinName = (pin: Pin) => (pin.leaf ? `${pin.key}.${pin.leaf}` : pin.key);
const moves = (name: string, pin: Pin) => name === pinName(pin) || (name === pin.key && !!pin.leaf);

/**
 * The overrides that make `sheet` defend exactly as `effective` says:
 * every key where the two disagree, plus the carried keys as they were.
 */
function overridesOver(sheet: Character, effective: Bag, carried: Overrides): Overrides {
  const resolved = cachedDefenderSheet(sheet) as unknown as Bag;
  const differing = Object.entries(effective).filter(
    ([key, value]) => value !== undefined && !sameValue(value, resolved[key]),
  );
  return { ...Object.fromEntries(differing), ...carried } as Overrides;
}

/** `c` after one move, when the move left fewer pins; `c` itself otherwise. */
function tried(c: Combatant, move: Move, pin: Pin, effective: Bag, carried: Overrides): Combatant {
  const sheet = move(c.sheet, pin);
  if (sheet === c.sheet) return c;
  const next = { ...c, sheet, overrides: overridesOver(sheet, effective, carried) };
  return listPins(next).length < listPins(c).length ? next : c;
}

/**
 * The combatant's sheet and overrides with every sayable pin folded into
 * the sheet. Returns the SAME halves when there is nothing to fold.
 */
export function backfillSheetFromPins(c: Combatant): Pick<Combatant, 'sheet' | 'overrides'> {
  const { carried, pinned } = splitCarried(c.overrides ?? {});
  const effective = { ...cachedDefenderSheet(c.sheet), ...pinned } as unknown as Bag;
  let current = c;
  for (const [name, move] of MOVES) {
    for (const pin of listPins(current).filter((p) => moves(name, p))) {
      current = tried(current, move, pin, effective, carried);
    }
  }
  return current === c
    ? { sheet: c.sheet, overrides: c.overrides }
    : { sheet: current.sheet, overrides: current.overrides };
}
